// Ported from vlookup-dramatic/app/faq/page.tsx by scripts/revamp/port-design.js,
// then wired to the live FAQ call (revamp plan task 16).
//
// Data: GET faq (homePageActions.loadFaqData) — the same call the old page made.
//   - every item has `type` = "General Queries" | "Mentor Queries" | "Mentee Queries",
//     which are exactly the design's three tabs (faqTabs), so items are grouped by type.
//   - `description` is CMS rich text (HTML, Bootstrap row/col markup) — rendered with
//     DOMPurify and a small prose style; the design's sample FAQs are only the
//     fallback while the call is loading or returns nothing.
//   - search: GET faq?keyword=… (same as the old page, keyword kept in ?keyword= so
//     the URL is shareable); results show as one flat list, "Reset" restores the tabs.
import React, { useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../../../utils/apiBase";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import DOMPurify from "dompurify";
import { ArrowUpRight, Search } from "../../lib/icons";
import { faqTabs, faqGeneral, faqMentor, faqMentee } from "../../lib/content";
import { ButtonLink } from "../../components/button-link";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { stripHtml, useJsonLd, usePageMeta } from "../../hooks/use-page-meta";
import * as homePageActions from "../../../actions/homePageActions";

// API `type` values -> tab. Labels come from the content file (identical strings).
const TABS = [
  { value: "general", label: faqTabs[0], type: "General Queries", fallback: faqGeneral },
  { value: "mentor", label: faqTabs[1], type: "Mentor Queries", fallback: faqMentor },
  { value: "mentee", label: faqTabs[2], type: "Mentee Queries", fallback: faqMentee },
];

// QA row 15d: CMS answers sometimes reference uploads with site-relative srcs
// ("/api/public/upload/…"). Those 404 on the review domain (and any host that
// isn't vlookup.ai), which is why "some images are missing" — point them at
// the API origin explicitly.
const API_ORIGIN = (() => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch (e) {
    return "";
  }
})();
const absolutiseUploads = (html) =>
  API_ORIGIN ? html.replace(/src="\/?api\//g, `src="${API_ORIGIN}/api/`) : html;

const fromApi = (item) => ({ id: item.id, q: item.title, html: absolutiseUploads(item.description || "") });

export default function FaqPage() {
  usePageMeta({
    title: "Frequently Asked Questions",
    description:
      "Want to know more about our unique mentoring platform? Read the frequently asked questions and reach out to us.",
  });

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const faqData = useSelector((s) => s.faqData) || [];

  const urlKeyword = new URLSearchParams(location.search).get("keyword") || "";
  const [query, setQuery] = useState(urlKeyword);
  const [tab, setTab] = useState("general");

  // Load (all, or the URL's keyword) on mount and whenever ?keyword= changes.
  useEffect(() => {
    setQuery(urlKeyword);
    dispatch(urlKeyword ? homePageActions.loadFaqData({ keyword: urlKeyword }) : homePageActions.loadFaqData());
  }, [dispatch, urlKeyword]);

  const submitSearch = (e) => {
    if (e) e.preventDefault();
    const k = query.trim();
    history.push({ pathname: location.pathname, search: k ? "?keyword=" + encodeURIComponent(k) : "" });
  };
  const resetSearch = () => {
    setQuery("");
    history.push({ pathname: location.pathname, search: "" });
  };

  const apiItems = useMemo(() => (Array.isArray(faqData) ? faqData.map(fromApi) : []), [faqData]);
  const byType = useMemo(() => {
    const m = {};
    if (Array.isArray(faqData)) faqData.forEach((it) => (m[it.type] = m[it.type] || []).push(fromApi(it)));
    return m;
  }, [faqData]);
  const searching = !!urlKeyword;

  const faqEntities = useMemo(() => {
    const seen = new Set();
    const entities = [];
    TABS.forEach((t) => {
      const list = (byType[t.type] && byType[t.type].length ? byType[t.type] : t.fallback) || [];
      list.forEach((f) => {
        if (!f.q || seen.has(f.q)) return;
        seen.add(f.q);
        const text = f.html
          ? stripHtml(f.html)
          : [f.a, ...(f.bullets || []), f.aAfter].filter(Boolean).join(" ");
        if (!text) return;
        entities.push({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text },
        });
      });
    });
    return entities;
  }, [byType]);

  useJsonLd(
    "ld-faq",
    faqEntities.length
      ? { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqEntities }
      : null
  );

  return (
    <main>
      {/* QA rows 15b/15c — CMS answer clean-up that utility classes can't
          reach (inline styles, Bootstrap .btn anchors, CKEditor floats):
          - link "boxes" become plain red underlined links
          - floated images are contained (no text overlap) and auto-fit */}
      <style>{`
        /* Enforce website typography (Plus Jakarta Sans) on FAQ questions and CMS HTML content */
        [data-slot="accordion-trigger"],
        [data-slot="accordion-trigger"] *,
        .faq-prose,
        .faq-prose *,
        .faq-prose .faq_text_content,
        .faq-prose .faq_text_content *,
        .faq-prose .list_style,
        .faq-prose .list_style * {
          font-family: var(--font-jakarta, "Plus Jakarta Sans"), "Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif !important;
        }
        .faq-prose { display: flow-root; }
        .faq-prose h1, .faq-prose h2, .faq-prose h3, .faq-prose h4, .faq-prose h5, .faq-prose h6 {
          font-family: var(--font-jakarta, "Plus Jakarta Sans"), "Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif !important;
          font-weight: 600 !important;
          color: #222222 !important;
        }
        .faq-prose a, .faq-prose a.btn, .faq-prose a[class*="btn-"] {
          background: transparent !important;
          border: 0 !important;
          box-shadow: none !important;
          padding: 0 !important;
          color: #ef4343 !important;
          font-weight: 600;
          text-decoration: underline !important;
          text-underline-offset: 3px;
          white-space: normal;
        }
        .faq-prose a:hover { color: #c92c2c !important; }
        .faq-prose img {
          max-width: 100% !important;
          height: auto !important;
          border-radius: 12px;
          margin: 6px 0 14px;
        }
        .faq-prose img[style*="float:left"], .faq-prose img[style*="float: left"] {
          margin: 4px 18px 12px 0;
          max-width: min(46%, 320px) !important;
        }
        .faq-prose img[style*="float:right"], .faq-prose img[style*="float: right"] {
          margin: 4px 0 12px 18px;
          max-width: min(46%, 320px) !important;
        }
        .faq-prose, .faq-prose p, .faq-prose li { overflow-wrap: break-word; }
        .faq-prose table { max-width: 100%; }
        /* PROSE pulls .row out with a -12px gutter but only hands .col-md-6 the
           matching padding back, so an answer built from any other column class
           (col-md-12, col-sm-6, col-lg-4, …) sat 12px outside the panel and the
           accordion's overflow-hidden cut its first/last characters off. */
        .faq-prose [class*="col-"] { padding-left: 12px; padding-right: 12px; }
      `}</style>
      {/* HERO */}
      <section className="relative overflow-hidden pt-12 md:pt-16">
        <div className="wrap relative">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-cream-deep px-6 py-16 text-center ring-1 ring-brand-navy/5 md:px-16 md:py-24">
            <div
              aria-hidden
              className="pointer-events-none absolute -bottom-24 left-1/2 h-72 w-[36rem] -translate-x-1/2 rounded-full bg-brand-ink/25 blur-2xl"
            />
            <div className="relative">
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-brand-red">Help Centre</p>
              <h1 className="mt-4 font-display text-5xl font-semibold leading-[1.02] text-heading md:text-8xl">
                How can we <em className="font-normal italic text-brand-red">help?</em>
              </h1>
              <p className="mx-auto mt-5 max-w-lg text-base text-muted-foreground md:text-lg">
                Find answers to common questions about mentoring, the platform, and getting started.
              </p>

              {/* Editorial search field — submits to GET faq?keyword= */}
              <form
                role="search"
                onSubmit={submitSearch}
                className="mx-auto mt-9 flex max-w-xl items-center gap-2 rounded-full bg-card p-2 shadow-lg ring-1 ring-brand-navy/10"
              >
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by question…"
                  aria-label="Search questions"
                  className="w-full bg-transparent px-5 py-3 text-base text-brand-navy outline-none placeholder:text-brand-navy/40"
                />
                <button
                  type="submit"
                  aria-label="Search"
                  className="flex size-12 shrink-0 items-center justify-center rounded-full bg-brand-red text-white transition-colors hover:bg-brand-red-dark"
                >
                  <Search className="size-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* SEARCH RESULTS (flat list) or TABS + ACCORDIONS */}
      {/* QA row 15a: the FAQ list uses the same container width as the hero
          panel above it (was a narrower max-w-3xl column). */}
      <section className="wrap py-14 md:py-20">
        <div>
          {searching ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  {apiItems.length} result{apiItems.length === 1 ? "" : "s"} for{" "}
                  <strong className="text-heading">“{urlKeyword}”</strong>
                </p>
                <button
                  type="button"
                  onClick={resetSearch}
                  className="text-sm font-bold text-brand-red underline underline-offset-4 hover:text-brand-red-dark"
                >
                  Reset search
                </button>
              </div>
              <div className="mt-8">
                {apiItems.length ? (
                  <FaqAccordion items={apiItems} idPrefix="search" />
                ) : (
                  <p className="rounded-2xl bg-cream-deep px-6 py-10 text-center text-muted-foreground">
                    No questions match that search. Try another word, or browse the tabs.
                  </p>
                )}
              </div>
            </>
          ) : (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="mx-auto flex h-auto! w-full max-w-xl gap-2 rounded-full bg-cream-deep p-1.5">
                {TABS.map((t) => (
                  <TabsTrigger
                    key={t.value}
                    value={t.value}
                    className="h-auto! flex-1 rounded-full px-3 py-3 text-xs font-bold text-heading data-active:bg-brand-red! data-active:text-white! sm:text-sm"
                  >
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {TABS.map((t) => (
                <TabsContent key={t.value} value={t.value} className="mt-10">
                  <FaqAccordion items={byType[t.type] && byType[t.type].length ? byType[t.type] : t.fallback} idPrefix={t.value} />
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="wrap pb-24">
        <div className="mx-auto max-w-3xl rounded-[2rem] bg-cream-deep px-8 py-14 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-brand-red">Still stuck?</p>
          <h2 className="mt-3 font-display text-3xl font-semibold text-heading md:text-5xl">
            Still have a <em className="font-normal italic text-brand-red">question?</em>
          </h2>
          <p className="mx-auto mt-4 max-w-md text-muted-foreground">
            We&rsquo;re happy to help. Reach out directly and we&rsquo;ll get back to you.
          </p>
          <ButtonLink
            href="mailto:support@vlookup.ai"
            className="group mx-auto mt-7 h-auto w-fit items-center gap-3 rounded-full bg-brand-red py-3 pl-6 pr-3 text-sm font-bold text-white transition-colors hover:bg-brand-red-dark"
          >
            Contact support@vlookup.ai
            <span className="flex size-8 items-center justify-center rounded-full bg-white/20 transition-transform group-hover:rotate-45">
              <ArrowUpRight className="size-4" />
            </span>
          </ButtonLink>
        </div>
      </section>
    </main>
  );
}

// CMS answers are rich text with Bootstrap row/col markup; Tailwind's preflight
// (scoped to .vl-revamp) strips list bullets and margins, so put them back here.
const PROSE =
  "[&_p]:mb-4 [&_p:last-child]:mb-0 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1.5 " +
  "[&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-heading [&_h4]:mt-3 [&_h4]:mb-1 [&_h4]:font-semibold [&_h4]:text-heading " +
  "[&_strong]:font-semibold [&_strong]:text-heading [&_a]:font-semibold [&_a]:text-brand-red [&_a]:underline [&_a]:underline-offset-3 [&_a:hover]:text-brand-red-dark " +
  "[&_.row]:flex [&_.row]:flex-wrap [&_.row]:-mx-3 [&_.col-md-6]:w-full [&_.col-md-6]:px-3 md:[&_.col-md-6]:w-1/2 [&_img]:my-4 [&_img]:max-w-full [&_img]:rounded-xl";

function FaqAccordion({ items, idPrefix }) {
  return (
    <Accordion openMultiple={false} className="flex flex-col gap-3">
      {items.map((f, i) => (
        <AccordionItem
          key={`${idPrefix}-${f.id != null ? f.id : i}`}
          value={`${idPrefix}-${f.id != null ? f.id : i}`}
          className="rounded-2xl border border-brand-navy/10 bg-card px-6 shadow-sm"
        >
          <AccordionTrigger className="gap-6 py-6 text-lg font-semibold text-heading hover:no-underline md:text-2xl">
            {f.q}
          </AccordionTrigger>
          <AccordionContent className="pb-6 text-base leading-relaxed text-muted-foreground">
            {f.html ? (
              <div
                className={`faq-prose ${PROSE}`}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(f.html, { ADD_ATTR: ["target"] }) }}
              />
            ) : (
              <>
                <p>{f.a}</p>
                {f.bullets && (
                  <ul className="mt-4 flex flex-col gap-2.5">
                    {f.bullets.map((b) => (
                      <li key={b} className="flex gap-2.5">
                        <span className="mt-0.5 shrink-0 text-brand-red">•</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {f.aAfter && <p className="mt-4">{f.aAfter}</p>}
              </>
            )}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
